import { jsonParseWithSchema } from "@aztec/foundation/json-rpc";
import { Button, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader } from "@heroui/react";
import { ChevronLeftIcon } from "lucide-react";
import { useState } from "react";
import type { Address, Hash } from "viem";
import { L2ToL1MembershipWitnessSchema } from "../../state/schemas";
import type { ManualClaimParams } from "../../state/types";
import { ManualClaimDepositForm } from "./ManualClaimDepositForm";
import { ManualClaimWithdrawForm } from "./ManualClaimWithdrawForm";

export function ManualClaimParamsModal({
  isOpen,
  onBack,
  onClose,
  claimType,
  recipientAddress,
  onClaim,
}: {
  isOpen: boolean;
  onBack: () => void;
  onClose: () => void;
  claimType: "deposit" | "withdraw";
  recipientAddress: Address | undefined;
  onClaim: (claimData: ManualClaimParams) => void;
}) {
  const [l1TokenAddress, setL1TokenAddress] = useState<Address | undefined>();
  const [amount, setAmount] = useState<bigint | undefined>();
  const [messageHash, setMessageHash] = useState<Hash | undefined>();
  const [messageIndex, setMessageIndex] = useState<bigint | undefined>();
  const [l2BlockNumber, setL2BlockNumber] = useState<number | undefined>();
  const [witness, setWitness] = useState<Hash | undefined>();
  const [message, setMessage] = useState<Hash | undefined>();
  const [l2WithdrawData, setL2WithdrawData] = useState<Hash | undefined>();

  function handleClaim() {
    if (!recipientAddress) {
      throw new Error("Recipient address is required");
    }

    if (!l1TokenAddress) {
      throw new Error("Token address is required");
    }

    if (!amount) {
      throw new Error("Amount is required");
    }

    if (!messageHash) {
      throw new Error("Message hash is required");
    }

    if (!messageIndex) {
      throw new Error("Message index is required");
    }

    if (!l2BlockNumber) {
      throw new Error("Block number is required");
    }

    if (!witness) {
      throw new Error("Witness is required");
    }

    if (!message) {
      throw new Error("Message is required");
    }

    if (!l2WithdrawData) {
      throw new Error("Withdraw data is required");
    }

    const timestamp = Date.now();
    const claimId = crypto.randomUUID();

    if (claimType === "deposit") {
      onClaim({
        tokenAddress: l1TokenAddress,
        hash: messageHash,
        index: messageIndex,
        l2BlockNumber,
        isManualClaim: true,
        bridgeType: "deposit",
        recipientAddress,
        amount,
        timestamp,
        id: claimId,
      });
    } else {
      onClaim({
        tokenAddress: l1TokenAddress,
        l2BlockNumber,
        witness: jsonParseWithSchema(Buffer.from(witness.replace(/^0x/, ""), "hex").toString("utf-8"), L2ToL1MembershipWitnessSchema),
        message,
        withdrawData: l2WithdrawData,
        isManualClaim: true,
        bridgeType: claimType,
        timestamp,
        id: claimId,
      });
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      isDismissable={false}
      isKeyboardDismissDisabled={true}
      classNames={{
        base: "turnstile-modal-border rounded-3xl",
        closeButton: "text-white text-xl top-4 right-5",
        header: "pt-5 pb-3",
        body: "items-center px-6 py-2 gap-4",
        backdrop: "turnstile-modal-backdrop",
      }}
    >
      <ModalContent>
        <ModalHeader className="flex flex-col items-start mr-6">
          <div className="flex items-center">
            <Button isIconOnly onPress={onBack} className="bg-transparent -ml-3">
              <ChevronLeftIcon className="text-[#ADC0D0]" />
            </Button>
            <p className="text-lg font-semibold">Complete the parameters</p>
          </div>
          <p className="text-sm font-normal text-[#ADC0D0]">
            Get neccessary information from L1 and L2 block explorers and paster them below.
          </p>
        </ModalHeader>
        <ModalBody>
          {claimType === "deposit" ? (
            <ManualClaimDepositForm
              l1TokenAddress={l1TokenAddress}
              amount={amount}
              messageHash={messageHash}
              messageIndex={messageIndex}
              l2BlockNumber={l2BlockNumber}
              setL1TokenAddress={setL1TokenAddress}
              setAmount={setAmount}
              setMessageHash={setMessageHash}
              setMessageIndex={setMessageIndex}
              setL2BlockNumber={setL2BlockNumber}
            />
          ) : (
            <ManualClaimWithdrawForm
              l1TokenAddress={l1TokenAddress}
              messageHash={messageHash}
              l2BlockNumber={l2BlockNumber}
              witness={witness}
              message={message}
              l2WithdrawData={l2WithdrawData}
              setL1TokenAddress={setL1TokenAddress}
              setMessageHash={setMessageHash}
              setL2BlockNumber={setL2BlockNumber}
              setWitness={setWitness}
              setMessage={setMessage}
              setL2WithdrawData={setL2WithdrawData}
            />
          )}
        </ModalBody>
        <ModalFooter>
          <Button fullWidth size="lg" color="primary" radius="full" onPress={handleClaim}>
            Claim
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
